import libphonenumber from 'google-libphonenumber'

const { PhoneNumberFormat, PhoneNumberUtil } = libphonenumber

/** libphonenumber region code (ISO 3166-1 alpha-2). */
type CountryCode = string

const phoneUtil = PhoneNumberUtil.getInstance()

const SE = 'SE'
const DEFAULT_COUNTRY: CountryCode = 'SE'

/**
 * Swedish PTS test / reserved ranges (not validated by libphonenumber metadata).
 * - NDC 655/656: provnummer / trial numbers (PTS numbering plan)
 * - 0100/0109: reserved block (not issued to subscribers; see PTS / damberg.one safe generator)
 */
export const isPtsSwedishTestNumber = (phone: string): boolean => {
  const normalized = normalizePhoneForParsing(phone).replace(/\s/g, '')
  const e164 = normalized.startsWith('+') ? normalized : `+${normalized.replace(/^0+/, '')}`
  return /^\+4665[56]\d{6}$/.test(e164) || /^\+4610[09]\d{6}$/.test(e164)
}

const toPtsSwedishTestE164 = (phone: string): string => {
  const normalized = normalizePhoneForParsing(phone).replace(/\s/g, '')
  return normalized.startsWith('+') ? normalized : `+${normalized.replace(/^0+/, '')}`
}

/**
 * Gets country code from client
 * Uses client.country if available, otherwise defaults to SE
 */
export const getCountryCodeFromClient = (client?: { country?: string } | null): CountryCode => {
  if (client?.country) {
    return client.country.toUpperCase() as CountryCode
  }
  return DEFAULT_COUNTRY
}

/**
 * Normalizes phone number by converting "00" prefix to "+"
 * Handles formats like 004612344 -> +4612344
 */
const normalizePhoneForParsing = (phone: string): string => {
  if (!phone) return phone

  // Convert "00" prefix to "+" (common international dialing prefix in Europe)
  // Only convert if it's at the start and followed by digits
  if (phone.startsWith('00') && phone.length > 2 && /^\d/.test(phone.substring(2))) {
    return '+' + phone.substring(2)
  }

  return phone
}

/**
 * Detects country code from phone number
 * Returns the detected country code or null if unable to detect
 */
export const detectCountryFromPhone = (phone: string): CountryCode | null => {
  if (!phone) return null

  try {
    // Normalize phone number (convert "00" to "+")
    const normalizedPhone = normalizePhoneForParsing(phone)

    // Try to parse without default country first (for international numbers)
    const parsedNumber = phoneUtil.parse(normalizedPhone, undefined)
    const countryCode = phoneUtil.getRegionCodeForNumber(parsedNumber)
    return countryCode as CountryCode | null
  } catch (e) {
    // If parsing fails, return null
    return null
  }
}

/**
 * Gets the country code to use for phone validation
 * Priority: detected country > default country
 */
export const getCountryForValidation = (phone: string, defaultCountry: CountryCode = DEFAULT_COUNTRY): CountryCode => {
  const detectedCountry = detectCountryFromPhone(phone)
  return detectedCountry || defaultCountry
}

/**
 * Validates phone number for a specific country
 * @param phone - Phone number to validate
 * @param defaultCountry - Default country code (defaults to SE)
 * @returns true if valid, false otherwise
 */
export const isValidNumber = (phone: string, defaultCountry: CountryCode = DEFAULT_COUNTRY) => {
  if (!phone) return false
  if (isPtsSwedishTestNumber(phone)) return true

  try {
    // Normalize phone number (convert "00" to "+")
    const normalizedPhone = normalizePhoneForParsing(phone)
    const countryCode = getCountryForValidation(normalizedPhone, defaultCountry)
    const parsedNumber = phoneUtil.parse(normalizedPhone, countryCode)
    return phoneUtil.isValidNumber(parsedNumber)
  } catch (e) {
    return false
  }
}

/**
 * Validates that a phone number belongs to a specific region (e.g. a country picked from a
 * dial-code selector), rejecting numbers that are individually valid but belong to another
 * region (e.g. a UK number while 'SE' is selected). Unlike `isValidNumber`, a `+`-prefixed
 * number is still auto-detected (so pasting a full international number works), but the
 * result must carry the *same dial code* as `regionCode` rather than merely being valid for
 * whichever region it happens to resolve to.
 *
 * Compares the numeric calling code (not `getRegionCodeForNumber`) because several regions
 * share one calling code — e.g. GB/GG/JE/IM all dial +44 — so a perfectly ordinary UK mobile
 * number can resolve to 'GG' instead of 'GB'; that's still the right country from the user's
 * perspective, since they picked it by dial code, not by jurisdiction.
 */
export const isValidNumberForRegion = (phone: string, regionCode: CountryCode): boolean => {
  if (!phone) return false
  if (regionCode === SE && isPtsSwedishTestNumber(phone)) return true

  try {
    const normalizedPhone = normalizePhoneForParsing(phone)
    const parsedNumber = phoneUtil.parse(normalizedPhone, regionCode)
    if (!phoneUtil.isValidNumber(parsedNumber)) return false
    return parsedNumber.getCountryCode() === phoneUtil.getCountryCodeForRegion(regionCode)
  } catch (e) {
    return false
  }
}

/**
 * Gets the country code that should be used for a phone number
 * This is useful for displaying the flag
 */
export const getPhoneCountryCode = (phone: string, defaultCountry: CountryCode = DEFAULT_COUNTRY): CountryCode => {
  return getCountryForValidation(phone, defaultCountry)
}

export const getInternationalPhoneNumber = (phone: string, defaultCountry: CountryCode = DEFAULT_COUNTRY) => {
  if (isPtsSwedishTestNumber(phone)) {
    return toPtsSwedishTestE164(phone)
  }

  try {
    // Normalize phone number (convert "00" to "+")
    const normalizedPhone = normalizePhoneForParsing(phone)
    const countryCode = getCountryForValidation(normalizedPhone, defaultCountry)
    const parsedNumber = phoneUtil.parse(normalizedPhone, countryCode)
    return phoneUtil.format(parsedNumber, PhoneNumberFormat.E164) // return phone in format +46123010203
  } catch (e) {
    return phone
  }
}

export const getPhoneForSwish = (phone: string) => {
  // 1. get phone in international format, ie. +46706112233
  // 2. trim trailing '+' from phone number
  return getInternationalPhoneNumber(phone).replace(/^\+/, '')
}

export const getPhoneForVippsMobile = (phone: string) => {
  // 1. get phone in international format, ie. +46706112233
  // 2. trim trailing '+' from phone number
  return getInternationalPhoneNumber(phone).replace(/^\+/, '')
}

/** Deterministic `enrollment_users` document ID derived from a normalized phone. */
export const enrollmentUserIdFromPhone = (phone: string): string => {
  const phoneDigits = getInternationalPhoneNumber(phone).replace(/\D/g, '')
  return `phone_${phoneDigits}`
}
